import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

// The only units the API structures (UnitOfMeasure). Other free text goes through the "Use …" row, so OCR values survive edits.
const COMMON_UNITS = ["kg", "g", "L", "mL"];

export interface UnitComboboxProps {
  /** Current free-text unit, or null/empty when unset. */
  value: string | null | undefined;
  onChange: (unit: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** The stored value is the raw free text; the backend derives the structured UnitOfMeasure from it. */
export function UnitCombobox({
  value,
  onChange,
  placeholder = "Unit…",
  disabled,
  className,
}: UnitComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const current = value?.trim() ?? "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COMMON_UNITS;
    return COMMON_UNITS.filter((u) => u.toLowerCase().includes(q));
  }, [search]);

  const typed = search.trim();
  const showCustom =
    typed.length > 0 && !COMMON_UNITS.some((u) => u.toLowerCase() === typed.toLowerCase());

  const commit = (unit: string) => {
    const v = unit.trim();
    onChange(v || null);
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full min-w-0 justify-between font-normal",
            !current && "text-muted-foreground",
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">{current || placeholder}</span>
          {current ? (
            <span
              role="button"
              aria-label="Clear unit"
              className="ml-2 rounded-full p-0.5 hover:bg-muted-foreground/20"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X className="size-3.5 opacity-60" />
            </span>
          ) : (
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) min-w-44 p-0" align="start">
        {/* We filter COMMON_UNITS ourselves and always keep the "Use …" row. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type or pick a unit…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!search.trim() && (
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onChange(null);
                    setSearch("");
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 size-4", !current ? "opacity-100" : "opacity-0")} />
                  <span className="text-muted-foreground">No unit</span>
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Units">
                {filtered.map((u) => (
                  <CommandItem key={u} value={u} onSelect={() => commit(u)}>
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        current.toLowerCase() === u.toLowerCase() ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1">{u}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCustom && (
              <CommandGroup heading="Custom">
                <CommandItem value={`custom:${typed}`} onSelect={() => commit(typed)}>
                  <Check className="mr-2 size-4 opacity-0" />
                  Use “{typed}”
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
